import { useState, useEffect } from 'react';
import { FileText, Save, ChevronDown, ChevronRight } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { useProject } from '@/context/ProjectContext';
import { toast } from 'sonner';

// ─── BP-00-A option lists ───────────────────────────────────────────────────

const PROJECT_TYPES = [
  'Brand Foundations Only',
  'Brand + Digital',
  'Brand + Physical',
  'Full Suite (All 3 Phases)',
];

const PRIMARY_MARKETS = ['Sydney AU', 'Beirut LB', 'Both Markets', 'Other'];

const TIMELINES = ['4–6 weeks', '8–10 weeks', '12–16 weeks', 'Flexible'];

const BUDGET_RANGES = [
  'Under $5K',
  '$5K–$15K',
  '$15K–$30K',
  '$30K–$60K',
  '$60K+',
  'TBD',
];

const DELIVERABLE_OPTIONS = [
  'Logo & Brand Identity',
  'Brand Guidelines Document',
  'Social Media Setup',
  'Website',
  'Mobile App',
  'Interior Design',
  'Signage',
  'Social Content Production',
];

const REFERRAL_SOURCES = [
  'Google Search',
  'Instagram',
  'LinkedIn',
  'Referral from Client',
  'Direct / Already knew Blueprints',
  'Other',
];

const ARABIC_LABELS = {
  projectType: 'نوع المشروع',
  primaryMarket: 'السوق الأساسي',
  estimatedTimeline: 'المدة المتوقعة',
  budgetRange: 'الميزانية',
  deliverableScope: 'نطاق التسليم',
  referralSource: 'مصدر الإحالة',
};

// ─── Shared sub-components ──────────────────────────────────────────────────

const ScopeSelect = ({ id, label, arabicLabel, value, onChange, options, arabicMode }) => (
  <div className="space-y-1">
    {arabicMode && arabicLabel && (
      <p className="font-mono text-[11px] lodge-accent">{arabicLabel}</p>
    )}
    <Label className="text-foreground/80">{label}</Label>
    <select
      id={id}
      value={value}
      onChange={(e) => onChange(e.target.value)}
      className="w-full rounded-md bg-black/[0.03] border border-border text-foreground px-3 py-2 text-sm focus:outline-none focus:ring-1 focus:ring-primary/50"
    >
      <option value="">— select —</option>
      {options.map((opt) => (
        <option key={opt} value={opt}>{opt}</option>
      ))}
    </select>
  </div>
);

// ─── Main component ─────────────────────────────────────────────────────────

// The canonical "describe your business" capture -- one place this gets typed
// once, instead of Naming/Strategy/Logo Studio each asking for it separately
// in their own blank text field every time. Naming/Strategy/Logo pre-fill
// their own intake from currentProject.description, editable from there.
const BusinessDescriptionPanel = ({ embedded = false }) => {
  const { currentProject, updateProject } = useProject();

  // Core description fields
  const [description, setDescription] = useState('');
  const [industry, setIndustry] = useState('');
  const [hasChanges, setHasChanges] = useState(false);
  const [saving, setSaving] = useState(false);

  // BP-00-A: Project Scope
  const [scopeOpen, setScopeOpen] = useState(false);
  const [arabicMode, setArabicMode] = useState(false);
  const [projectType, setProjectType] = useState('');
  const [primaryMarket, setPrimaryMarket] = useState('');
  const [estimatedTimeline, setEstimatedTimeline] = useState('');
  const [budgetRange, setBudgetRange] = useState('');
  const [deliverableScope, setDeliverableScope] = useState([]);
  const [referralSource, setReferralSource] = useState('');

  useEffect(() => {
    if (currentProject) {
      setDescription(currentProject.description || '');
      setIndustry(currentProject.industry || '');
      setProjectType(currentProject.project_type || '');
      setPrimaryMarket(currentProject.primary_market || '');
      setEstimatedTimeline(currentProject.estimated_timeline || '');
      setBudgetRange(currentProject.budget_range || '');
      setDeliverableScope(currentProject.deliverable_scope || []);
      setReferralSource(currentProject.referral_source || '');
      setHasChanges(false);
      setArabicMode(false);
    }
  }, [currentProject?.id]);

  const markChanged = () => setHasChanges(true);

  const toggleDeliverable = (item) => {
    setDeliverableScope((prev) =>
      prev.includes(item) ? prev.filter((d) => d !== item) : [...prev, item]
    );
    markChanged();
  };

  const handleSave = async () => {
    try {
      setSaving(true);
      await updateProject(currentProject.id, {
        description,
        industry,
        project_type: projectType,
        primary_market: primaryMarket,
        estimated_timeline: estimatedTimeline,
        budget_range: budgetRange,
        deliverable_scope: deliverableScope,
        referral_source: referralSource,
      });
      setHasChanges(false);
      toast.success('Business description saved');
    } catch (error) {
      toast.error('Failed to save');
    } finally {
      setSaving(false);
    }
  };

  if (!currentProject) return null;

  const isBeirut = primaryMarket === 'Beirut LB';
  const rtl = arabicMode && isBeirut;

  const scopeSection = (
    <div className="mt-5 border-t border-border pt-5">
      {/* Collapsible header */}
      <button
        type="button"
        onClick={() => setScopeOpen((o) => !o)}
        className="flex items-center gap-2 w-full text-left"
      >
        {scopeOpen ? (
          <ChevronDown className="w-4 h-4 text-muted-foreground shrink-0" />
        ) : (
          <ChevronRight className="w-4 h-4 text-muted-foreground shrink-0" />
        )}
        <span className="font-mono text-xs uppercase tracking-wide lodge-accent">
          Project Scope (BP-00-A)
        </span>
      </button>

      {scopeOpen && (
        <div className="mt-4 space-y-4" dir={rtl ? 'rtl' : 'ltr'}>
          {/* Arabic market note */}
          {isBeirut && (
            <div className="flex items-center justify-between gap-3 p-3 bg-black/[0.03] border border-border rounded-md">
              <p className="text-sm text-muted-foreground font-mono" dir="rtl">
                هذا المشروع مخصص للسوق اللبناني — النماذج متاحة بالعربية
              </p>
              <Button
                size="sm"
                type="button"
                variant="outline"
                onClick={() => setArabicMode((m) => !m)}
                className="shrink-0 bg-black/[0.03] border-border text-foreground hover:bg-black/[0.06] font-mono text-xs"
              >
                {arabicMode ? 'English' : 'عرض بالعربية'}
              </Button>
            </div>
          )}

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <ScopeSelect
              id="scope-project-type"
              label="Project Type"
              arabicLabel={ARABIC_LABELS.projectType}
              value={projectType}
              onChange={(v) => { setProjectType(v); markChanged(); }}
              options={PROJECT_TYPES}
              arabicMode={arabicMode}
            />
            <ScopeSelect
              id="scope-primary-market"
              label="Primary Market"
              arabicLabel={ARABIC_LABELS.primaryMarket}
              value={primaryMarket}
              onChange={(v) => { setPrimaryMarket(v); markChanged(); if (v !== 'Beirut LB') setArabicMode(false); }}
              options={PRIMARY_MARKETS}
              arabicMode={arabicMode}
            />
            <ScopeSelect
              id="scope-timeline"
              label="Estimated Timeline"
              arabicLabel={ARABIC_LABELS.estimatedTimeline}
              value={estimatedTimeline}
              onChange={(v) => { setEstimatedTimeline(v); markChanged(); }}
              options={TIMELINES}
              arabicMode={arabicMode}
            />
            <ScopeSelect
              id="scope-budget"
              label="Budget Range"
              arabicLabel={ARABIC_LABELS.budgetRange}
              value={budgetRange}
              onChange={(v) => { setBudgetRange(v); markChanged(); }}
              options={BUDGET_RANGES}
              arabicMode={arabicMode}
            />
          </div>

          {/* Deliverables Scope multi-checkbox */}
          <div className="space-y-2">
            {arabicMode && (
              <p className="font-mono text-[11px] lodge-accent">{ARABIC_LABELS.deliverableScope}</p>
            )}
            <Label className="text-foreground/80">Deliverables Scope</Label>
            <div className="grid grid-cols-2 gap-2">
              {DELIVERABLE_OPTIONS.map((item) => (
                <label
                  key={item}
                  className="flex items-center gap-2 cursor-pointer select-none text-sm text-foreground"
                >
                  <input
                    type="checkbox"
                    checked={deliverableScope.includes(item)}
                    onChange={() => toggleDeliverable(item)}
                    className="accent-primary"
                  />
                  {item}
                </label>
              ))}
            </div>
          </div>

          <ScopeSelect
            id="scope-referral"
            label="Referral Source"
            arabicLabel={ARABIC_LABELS.referralSource}
            value={referralSource}
            onChange={(v) => { setReferralSource(v); markChanged(); }}
            options={REFERRAL_SOURCES}
            arabicMode={arabicMode}
          />
        </div>
      )}
    </div>
  );

  const content = (
    <div className={embedded ? 'glass rounded-2xl p-6 border border-border mb-6' : 'glass rounded-2xl p-6 border border-border'}>
      <div className="flex items-center justify-between gap-4 mb-6">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-xl bg-primary/10 flex items-center justify-center">
            <FileText className="w-5 h-5 text-primary" />
          </div>
          <div>
            <h3 className="font-heading text-lg text-foreground">{embedded ? 'Business Overview' : 'Describe Your Business'}</h3>
            <p className="text-sm text-muted-foreground">Every other tab pre-fills from this &mdash; describe it once, edit downstream only if a tab needs something more specific</p>
          </div>
        </div>
        <Button
          size="sm"
          onClick={handleSave}
          disabled={saving || !hasChanges}
          className="bg-primary text-primary-foreground font-semibold hover:bg-primary/90 disabled:opacity-50"
          data-testid="save-business-description-btn"
        >
          <Save className="w-3.5 h-3.5 mr-1.5" />
          Save
        </Button>
      </div>

      <div className="space-y-4">
        <div className="space-y-2">
          <Label className="text-foreground/80">What does this business do?</Label>
          <textarea
            value={description}
            onChange={(e) => { setDescription(e.target.value); markChanged(); }}
            placeholder="e.g., A subscription coffee brand for remote software developers, focused on focus/energy and a minimalist aesthetic"
            rows={4}
            className="w-full rounded-md bg-black/[0.03] border border-border text-foreground placeholder:text-muted-foreground/70 px-3 py-2 text-sm focus:outline-none focus:ring-1 focus:ring-primary/50"
            data-testid="business-description-input"
          />
        </div>
        <div className="space-y-2">
          <Label className="text-foreground/80">Industry</Label>
          <Input
            value={industry}
            onChange={(e) => { setIndustry(e.target.value); markChanged(); }}
            placeholder="e.g., Food & Beverage"
            className="bg-black/[0.03] border-border text-foreground placeholder:text-muted-foreground/70"
            data-testid="business-industry-input"
          />
        </div>
      </div>

      {scopeSection}
    </div>
  );

  if (embedded) return content;

  return (
    <div className="h-full overflow-auto p-6" data-testid="business-description-panel">
      <div className="max-w-3xl mx-auto">
        <div className="mb-8">
          <div className="mb-6">
            <h2 className="font-heading text-2xl text-foreground mb-2">Your Business</h2>
            <p className="text-muted-foreground">Start here &mdash; this feeds every other tab automatically</p>
          </div>
          {content}
        </div>
      </div>
    </div>
  );
};

export default BusinessDescriptionPanel;
